import React, { useEffect, useRef, useState } from 'react';
import { whyUsContent } from '../data/mockData';
import { CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import logoImage from '../assets/logo_girl.png';

const useReveal = (delay = 0) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setTimeout(() => setVis(true), delay); obs.disconnect(); }
    }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
};

const WhyUs = () => {
  const [topRef,   topVis]   = useReveal(0);
  const [imgRef,   imgVis]   = useReveal(80);
  const [listRef,  listVis]  = useReveal(0);
  const { t } = useTranslation();

  const reasons = [
    { titleKey: 'whyUs.qualityTitle',       descKey: 'whyUs.qualityDesc' },
    { titleKey: 'whyUs.manufacturingTitle', descKey: 'whyUs.manufacturingDesc' },
    { titleKey: 'whyUs.innovationTitle',    descKey: 'whyUs.innovationDesc' },
    { titleKey: 'whyUs.portfolioTitle',     descKey: 'whyUs.portfolioDesc' },
    { titleKey: 'whyUs.careTitle',          descKey: 'whyUs.careDesc' },
  ];

  return (
    <section className="wu-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Playfair+Display:wght@700;800&display=swap');

        @keyframes wuUp   { from{opacity:0;transform:translateY(40px)} to{opacity:1;transform:translateY(0)} }
        @keyframes wuInR  { from{opacity:0;transform:translateX(48px)} to{opacity:1;transform:translateX(0)} }
        @keyframes wuBadge{ 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }

        .wu-root {
          font-family: 'Outfit', sans-serif;
          background: linear-gradient(180deg, #fff 0%, #f0f6ff 60%, #fff 100%);
          padding: 100px 0;
          position: relative; overflow: hidden;
        }

        /* Background blobs */
        .wu-blob {
          position: absolute; border-radius: 50%; pointer-events: none; filter: blur(80px);
        }
        .wu-blob-1 { width:500px;height:500px;background:rgba(58,143,163,0.05);top:-150px;right:-100px; }
        .wu-blob-2 { width:360px;height:360px;background:rgba(90,171,154,0.05);bottom:-80px;left:-60px; }

        .wu-inner { max-width: 1280px; margin: 0 auto; padding: 0 40px; position: relative; z-index: 1; }

        /* ── TOP GRID: header left, image right ── */
        .wu-top {
          display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center;
          margin-bottom: 72px;
        }
        @media(max-width:900px){ .wu-top{grid-template-columns:1fr; gap:40px;} }

        .wu-top-left {
          opacity: 0; transform: translateY(40px);
          transition: all 0.85s cubic-bezier(0.4,0,0.2,1);
        }
        .wu-top-left.in { opacity: 1; transform: translateY(0); }

        .wu-eyebrow {
          display: inline-block; font-size: 0.78rem; font-weight: 600;
          letter-spacing: 0.12em; text-transform: uppercase;
          color: #3a8fa3; background: rgba(58,143,163,0.08);
          border: 1px solid rgba(58,143,163,0.22);
          padding: 5px 16px; border-radius: 999px; margin-bottom: 20px;
        }
        .wu-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(1.55rem, 2.8vw, 2.2rem); font-weight: 800;
          color: #1a3a4a; margin: 0 0 40px; line-height: 1.2;
        }

        /* Mini stats below title */
        .wu-mini-stats { display: flex; gap: 28px; }
        .wu-mini-stat {}
        .wu-mini-val {
          font-family: 'Playfair Display', serif;
          font-size: 1.9rem; font-weight: 800; color: #3a8fa3; line-height: 1;
        }
        .wu-mini-lbl { font-size: 0.75rem; color: #64748b; font-weight: 500; margin-top: 3px; }

        /* Image side */
        .wu-top-right {
          position: relative;
          opacity: 0; transform: translateX(48px);
          transition: all 0.85s cubic-bezier(0.4,0,0.2,1) 0.18s;
        }
        .wu-top-right.in { opacity: 1; transform: translateX(0); }

        .wu-img-frame {
          border-radius: 28px; overflow: hidden;
          box-shadow: 0 24px 64px rgba(0,0,0,0.12), 0 8px 24px rgba(0,0,0,0.07);
          transition: transform 0.45s cubic-bezier(0.4,0,0.2,1), box-shadow 0.45s;
        }
        .wu-img-frame:hover {
          transform: translateY(-8px);
          box-shadow: 0 36px 80px rgba(0,0,0,0.18), 0 8px 24px rgba(58,143,163,0.1);
        }
        .wu-img-frame img { width: 100%; height: auto; display: block; }

        /* Badge */
        .wu-badge {
          position: absolute; bottom: -20px; right: -16px;
          background: rgba(255,255,255,0.88);
          backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.95);
          border-radius: 20px; padding: 16px 22px;
          box-shadow: 0 12px 40px rgba(58,143,163,0.18);
          display: flex; align-items: center; gap: 14px;
          animation: wuBadge 4.5s ease-in-out infinite;
        }
        .wu-badge-ring {
          width: 48px; height: 48px; border-radius: 50%; flex-shrink: 0;
          background: linear-gradient(135deg, #3a8fa3, #5aab9a);
          display: flex; align-items: center; justify-content: center;
          font-size: 1.4rem;
          box-shadow: 0 6px 18px rgba(58,143,163,0.4);
        }
        .wu-badge-big   { font-family: 'Playfair Display', serif; font-size: 1.2rem; font-weight: 800; color: #1a3a4a; }
        .wu-badge-small { font-size: 0.75rem; color: #64748b; }

        /* ── REASONS: 2-column grid of cards ── */
        .wu-reasons {
          display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
          opacity: 0; transform: translateY(36px);
          transition: all 0.85s cubic-bezier(0.4,0,0.2,1);
        }
        @media(max-width:700px){ .wu-reasons{grid-template-columns:1fr;} }
        .wu-reasons.in { opacity: 1; transform: translateY(0); }

        /* Last card if odd count: full width */
        .wu-reason:last-child:nth-child(odd) { grid-column: 1 / -1; }

        .wu-reason {
          display: flex; align-items: flex-start; gap: 16px;
          padding: 22px 24px; border-radius: 18px;
          background: rgba(255,255,255,0.65);
          backdrop-filter: blur(18px) saturate(1.5);
          -webkit-backdrop-filter: blur(18px) saturate(1.5);
          border: 1px solid rgba(255,255,255,0.88);
          box-shadow: 0 2px 12px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.9);
          transition: all 0.35s cubic-bezier(0.4,0,0.2,1);
          cursor: default; position: relative; overflow: hidden;
        }
        /* Left accent bar */
        .wu-reason::before {
          content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px;
          background: linear-gradient(180deg, #3a8fa3, #5aab9a);
          opacity: 0; transition: opacity 0.3s;
        }
        .wu-reason:hover {
          background: rgba(255,255,255,0.92);
          box-shadow: 0 10px 32px rgba(58,143,163,0.14);
          border-color: rgba(58,143,163,0.22);
          transform: translateX(8px);
        }
        .wu-reason:hover::before { opacity: 1; }

        /* Icon wrapper */
        .wu-reason-icon {
          width: 40px; height: 40px; border-radius: 12px; flex-shrink: 0;
          background: rgba(58,143,163,0.09);
          border: 1px solid rgba(58,143,163,0.18);
          display: flex; align-items: center; justify-content: center;
          transition: background 0.3s, box-shadow 0.3s;
        }
        .wu-reason:hover .wu-reason-icon {
          background: rgba(58,143,163,0.16);
          box-shadow: 0 4px 12px rgba(58,143,163,0.2);
        }
        .wu-reason-icon svg { color: #3a8fa3; width: 20px; height: 20px; }
        .wu-reason-h  { font-size: 1rem; font-weight: 700; color: #1a3a4a; margin-bottom: 4px; }
        .wu-reason-p  { font-size: 0.88rem; color: #64748b; line-height: 1.6; margin: 0; }
      `}</style>

      <div className="wu-blob wu-blob-1" />
      <div className="wu-blob wu-blob-2" />

      <div className="wu-inner">
        {/* TOP */}
        <div className="wu-top">
          <div ref={topRef} className={`wu-top-left ${topVis ? 'in' : ''}`}>
            <div className="wu-eyebrow">{t("whyUs.subtitle")}</div>
            <h2 className="wu-title">{t("whyUs.title")}</h2>
            <div className="wu-mini-stats">
              {/* {[['15+', t("about.yearsexperience")], ['50+', t("about.countriesserved")], ['100%', t("about.qualitycertified")]].map(([v,l],i) => (
                <div key={i} className="wu-mini-stat">
                  <div className="wu-mini-val">{v}</div>
                  <div className="wu-mini-lbl">{l}</div>
                </div>
              ))} */}
            </div>
          </div>

          <div ref={imgRef} className={`wu-top-right ${imgVis ? 'in' : ''}`}>
            <div className="wu-img-frame">
              <img src={logoImage} alt="Why Us" />
            </div>
            <div className="wu-badge">
              <div className="wu-badge-ring">✓</div>
              <div>
                <div className="wu-badge-big">{t("globalTrust.title")}</div>
                <div className="wu-badge-small">{t("globalTrust.subtitle")}</div>
              </div>
            </div>
          </div>
        </div>

        {/* REASONS 2-COL GRID */}
        <div ref={listRef} className={`wu-reasons ${listVis ? 'in' : ''}`}>
          {reasons.map((r, i) => (
            <div key={i} className="wu-reason" style={{ transitionDelay: `${i * 60}ms` }}>
              <div className="wu-reason-icon"><CheckCircle /></div>
              <div>
                <div className="wu-reason-h">{t(r.titleKey)}</div>
                <p className="wu-reason-p">{t(r.descKey)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyUs;